import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Mail, Phone, Shield, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import DemoRequestModal from "./DemoRequestModal";

export default function Layout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const location = useLocation();

  const handleLogin = () => {
    window.open('https://praqio.net', '_blank');
  };

  const navLinks = [
    { href: "/features", label: "Features" },
    { href: "/pricing", label: "Pricing" },
    { href: "/faq", label: "FAQ" },
    { href: "/contact", label: "Contact" }
  ];

  const isActive = (href) => location.pathname === href;

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Demo Request Modal */}
      <DemoRequestModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />

      {/* Navigation */}
      <nav className="nav-glass fixed top-0 left-0 right-0 z-40" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_praqio-portal/artifacts/6i1tcg3y_Untitled%20design.png" 
                alt="Praqio Logo" 
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              {navLinks.map((link) => (
                <Link 
                  key={link.href}
                  to={link.href} 
                  className={`transition-colors text-sm lg:text-base ${
                    isActive(link.href) 
                      ? 'text-[#1A362D] font-medium' 
                      : 'text-[#5C6C65] hover:text-[#1A362D]'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Button variant="ghost" className="text-[#1A362D] text-sm" data-testid="nav-login" onClick={handleLogin}>
                Log in
              </Button>
              <Button 
                className="btn-pill btn-primary text-sm px-5 py-2"
                data-testid="nav-demo"
                onClick={() => setDemoModalOpen(true)}
              >
                Request Demo
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden mt-4 pb-4"
                data-testid="mobile-menu"
              >
                <div className="flex flex-col gap-3">
                  {navLinks.map((link) => (
                    <Link 
                      key={link.href}
                      to={link.href} 
                      className={`py-2 transition-colors ${
                        isActive(link.href) 
                          ? 'text-[#1A362D] font-medium' 
                          : 'text-[#5C6C65] hover:text-[#1A362D]'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <hr className="border-[#E2E8D8] my-2" />
                  <Button variant="ghost" className="text-[#1A362D] justify-start" onClick={() => { handleLogin(); setMobileMenuOpen(false); }}>
                    Log in
                  </Button>
                  <Button 
                    className="btn-pill btn-primary"
                    onClick={() => { setDemoModalOpen(true); setMobileMenuOpen(false); }}
                  >
                    Request Demo
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="py-12 sm:py-16 px-4 sm:px-6 border-t border-[#E2E8D8] bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 sm:gap-8 mb-10">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_praqio-portal/artifacts/6i1tcg3y_Untitled%20design.png" 
                  alt="Praqio Logo" 
                  className="w-14 h-14 rounded-full object-cover"
                />
              </Link>
              <p className="text-sm text-[#5C6C65] mb-4">
                Streamline your credentialing workflow with intelligent automation.
              </p>
              <p className="text-xs text-[#8A9A86] mb-4">
                A product of <span className="font-medium text-[#1A362D]">Capital Billing Services, Inc.</span>
              </p>
              <div className="flex gap-3">
                <a href="mailto:hello@praqio.com" className="w-9 h-9 rounded-full bg-[#F9F6F0] flex items-center justify-center hover:bg-[#1A362D] hover:text-white transition-colors text-[#5C6C65]">
                  <Mail size={16} />
                </a>
                <a href="tel:+18776145227" className="w-9 h-9 rounded-full bg-[#F9F6F0] flex items-center justify-center hover:bg-[#1A362D] hover:text-white transition-colors text-[#5C6C65]">
                  <Phone size={16} />
                </a>
              </div>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-medium text-[#1A362D] mb-3 text-sm">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/features" className="footer-link text-sm">Features</Link></li>
                <li><Link to="/pricing" className="footer-link text-sm">Pricing</Link></li>
                <li><a href="#" className="footer-link text-sm">Security</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-medium text-[#1A362D] mb-3 text-sm">Company</h4>
              <ul className="space-y-2">
                <li><a href="https://www.capitalbillingservices.com/" target="_blank" rel="noopener noreferrer" className="footer-link text-sm">About Capital Billing Services, Inc.</a></li>
                <li><Link to="/contact" className="footer-link text-sm">Contact</Link></li>
                <li><a href="#" className="footer-link text-sm">Privacy Policy</a></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="font-medium text-[#1A362D] mb-3 text-sm">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/faq" className="footer-link text-sm">FAQ</Link></li>
                <li><a href="#" className="footer-link text-sm">Help Center</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-medium text-[#1A362D] mb-3 text-sm">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="footer-link text-sm">Privacy Policy</a></li>
                <li><a href="#" className="footer-link text-sm">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 border-t border-[#E2E8D8] flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-[#5C6C65]">
              © 2026 Praqio by Capital Billing Services, Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-4 sm:gap-6">
              <span className="flex items-center gap-1.5 text-xs text-[#5C6C65]">
                <Shield size={14} className="text-[#8A9A86]" />
                HIPAA Compliant
              </span>
              <span className="flex items-center gap-1.5 text-xs text-[#5C6C65]">
                <Lock size={14} className="text-[#8A9A86]" />
                SOC 2 Type II
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
